import { findLevel, findTopic, type OgeTopic, type OgeTopicPick } from "@edu/shared";

/**
 * Второй вход в форму выдачи: состав заданий, пришедший адресом (6.3
 * docs/student-errors-design.md).
 *
 * Формат: `слаг:уровень:количество`, через запятую, например
 * `fraction-arithmetic:1:3,quadratic-equations:2:3`. Порядок в параметре это
 * порядок строк в форме, и он значимый: диагностический лист идёт от основ,
 * названная преподавателем тема стоит последней (4.1 проекта). Пересортировать
 * его здесь значит отменить решение молча.
 *
 * ⚠️ **Разбор МЯГКИЙ, и это записанное решение, а не снисходительность.**
 * Ссылку кладут в закладки и открывают через полгода, когда у темы не стало
 * уровня, а потолок количества уехал вниз. Незнакомый слаг, несуществующий
 * уровень и мусор вместо чисел негодную строку выбрасывают, слишком большое
 * количество подрезают, а форма открывается с тем, что уцелело. Строгий разбор
 * превратил бы сохранённую ссылку в пустой экран с отказом.
 *
 * ⚠️ Отдельным модулем от формы по правилу раздела 9 CLAUDE.md: тестов на
 * компоненты в проекте нет, а границы разбора проверить надо, и проверяются
 * они только тогда, когда живут в чистой функции.
 */

/** Строка адреса после синтаксического разбора: справочник ещё не спрошен. */
export interface RawUrlPick {
  readonly skill: string;
  readonly difficulty: number;
  readonly count: number;
}

/** Что уцелело от адреса и что с ним случилось по дороге. */
export interface UrlPicksFit {
  /** Готово для `topics.restore`. Пустой массив это просто пустая форма. */
  readonly picks: OgeTopicPick[];
  /** Сколько строк выброшено: темы нет, уровня нет, мусор вместо чисел. */
  readonly dropped: number;
  /** Сколько строк подрезано по потолку темы или по потолку домашки. */
  readonly trimmed: number;
}

/**
 * Синтаксический разбор: три куска через двоеточие, целые числа во втором и
 * третьем. Про темы и потолки здесь не знают ничего, это следующий шаг.
 *
 * Пустые куски (`a:1:3,,b:1:3` и хвостовая запятая) не считаются негодными
 * строками: их в адресе не было, считать их потерей значит соврать в подписи.
 */
export function parseUrlPicks(raw: string | null): {
  readonly picks: RawUrlPick[];
  readonly dropped: number;
} {
  if (raw === null || raw.trim() === "") return { picks: [], dropped: 0 };
  const picks: RawUrlPick[] = [];
  let dropped = 0;
  for (const chunk of raw.split(",")) {
    const piece = chunk.trim();
    if (piece === "") continue;
    const parts = piece.split(":");
    if (parts.length !== 3) {
      dropped += 1;
      continue;
    }
    const skill = (parts[0] ?? "").trim();
    const difficulty = intOf(parts[1] ?? "");
    const count = intOf(parts[2] ?? "");
    if (skill === "" || difficulty === null || count === null || count < 1) {
      dropped += 1;
      continue;
    }
    picks.push({ skill, difficulty, count });
  }
  return { picks, dropped };
}

/**
 * Строки адреса против живого справочника тем и обоих потолков.
 *
 * ⚠️ Потолка два, и оба обязательны, ровно как у поля количества в форме
 * (`maxCountFor` в `@edu/shared`): потолок пары «тема и уровень» и остаток
 * общего потолка домашки. Считаются они по уже принятым строкам, поэтому две
 * строки об одной теме делят один потолок: сервер сложит их в один заказ
 * (`mergePicks`), и разбор, который об этом не знает, отдал бы форме состав,
 * который она сама объявит негодным.
 *
 * ⚠️ Уровень, которого у темы больше нет, строку ВЫБРАСЫВАЕТ, а не подменяет
 * умолчанием. Подмена вернула бы лист, который выглядит целым и при этом собран
 * не по тому уровню, а тихо не тот лист хуже, чем честно короткий.
 */
export function fitUrlPicks(
  raw: string | null, topics: readonly OgeTopic[], hardCap: number,
): UrlPicksFit {
  const parsed = parseUrlPicks(raw);
  const picks: OgeTopicPick[] = [];
  let dropped = parsed.dropped;
  let trimmed = 0;

  for (const want of parsed.picks) {
    const topic = findTopic(topics, want.skill);
    const level = topic === null ? null : findLevel(topic, want.difficulty);
    if (level === null) {
      dropped += 1;
      continue;
    }
    const takenAll = picks.reduce((sum, p) => sum + p.count, 0);
    const takenSame = picks.reduce(
      (sum, p) =>
        p.skill === want.skill && p.difficulty === want.difficulty ? sum + p.count : sum,
      0,
    );
    const room = Math.min(level.maxCount - takenSame, hardCap - takenAll);
    // Места не осталось вовсе: строка на ноль заданий это не строка
    if (room < 1) {
      dropped += 1;
      continue;
    }
    const count = Math.min(want.count, room);
    if (count < want.count) trimmed += 1;
    picks.push({ skill: want.skill, difficulty: want.difficulty, count });
  }

  return { picks, dropped, trimmed };
}

/**
 * Одна строка про то, что с адресом было не так. `null` означает «всё уцелело»,
 * и тогда форма молчит: сообщение «ничего не потеряно» это шум.
 *
 * ⚠️ Числа названы вслух, а слово «ссылка» стоит в тексте намеренно: строки в
 * форме преподаватель не заказывал руками, и без указания на источник пропажа
 * читается как поломка формы.
 */
export function urlPicksNote(fit: UrlPicksFit): string | null {
  const parts: string[] = [];
  if (fit.dropped > 0) {
    parts.push(
      fit.dropped === 1
        ? "одна тема из ссылки больше недоступна и не добавлена"
        : `тем из ссылки больше недоступно: ${fit.dropped}`,
    );
  }
  if (fit.trimmed > 0) {
    parts.push(
      fit.trimmed === 1
        ? "в одной строке количество уменьшено до допустимого"
        : `количество уменьшено до допустимого в строках: ${fit.trimmed}`,
    );
  }
  if (parts.length === 0) return null;
  return `${capitalize(parts.join(", "))}. Состав можно поправить руками.`;
}

/**
 * Название работы по составу из адреса: тема, ради которой лист собран, стоит в
 * составе ПОСЛЕДНЕЙ (4.1 проекта).
 *
 * Возвращает `null`, когда названия темы нет: справочник её не знает, а
 * выдумывать название по слагу значит показать преподавателю строку из кода.
 */
export function urlPicksTitle(
  picks: readonly OgeTopicPick[], topics: readonly OgeTopic[],
): string | null {
  const last = picks[picks.length - 1];
  if (last === undefined) return null;
  const topic = findTopic(topics, last.skill);
  if (topic === null) return null;
  return `Повторение перед темой «${topic.name}»`;
}

/**
 * Подпись предпросмотра, объясняющая порядок.
 *
 * ⚠️ **Своя, а не серверная, и это уступка.** Ручка состава листа отдаёт готовый
 * текст полем `note` (6.2 проекта) ровно затем, чтобы у второго предмета это
 * была правка контракта, а не строки в компоненте. Но панель «Где дыры» кладёт
 * в адрес только `picks`, а панель это чужая работа, поэтому дотянуться до
 * `note` форме нечем. Как только он приедет в адресе параметром, эта строка
 * уступает ему место: форма предпочитает серверный текст своему.
 */
export const SHEET_ORDER_NOTE =
  "Порядок не случайный: сначала темы, на которых стоит выбранная, она сама последней.";

function intOf(raw: string): number | null {
  const text = raw.trim();
  // Ручной предикат вместо `Number()`: тот считает числом и пустую строку, и
  // «1.5», и «1e2», а в адресе такое значит опечатку, а не количество
  if (!/^-?\d+$/.test(text)) return null;
  const value = Number(text);
  return Number.isSafeInteger(value) ? value : null;
}

function capitalize(text: string): string {
  return text.length === 0 ? text : text[0]!.toUpperCase() + text.slice(1);
}
